// CVC (Consonant-Vowel-Consonant) real-word assessment lists.
// Independently authored — not derived from any proprietary stimulus set.
// Curated for early decoding instruction Pre-K through Grade 3 intervention.
// All words are 3 letters, real English, classroom-appropriate.

export const CVC_GRADE_LEVELS = ['preK', 'kindergarten', 'grade1', 'grade2_3_intervention', 'mixed'];

export const CVC_GRADE_LABELS = {
  preK:                   'Pre-K',
  kindergarten:           'Kindergarten',
  grade1:                 'Grade 1',
  grade2_3_intervention:  'Grade 2–3 (Intervention)',
  mixed:                  'Mixed (all grades)',
};

// Pre-K (10 words) — short A and short I only. The earliest decoders
// blend the simplest CVC patterns; we limit to two vowels so they get
// enough repetition per vowel to feel mastery.
const PRE_K = [
  'cat', 'hat', 'mat', 'bat', 'sat',
  'big', 'pig', 'sit', 'hit', 'win',
];

// Kindergarten (25 words) — 5 words per short vowel (a, e, i, o, u).
// Standard K-level decoding measure. Most common, most universally
// recognized CVC words.
const KINDERGARTEN = [
  // short A
  'cat', 'hat', 'mat', 'fan', 'can',
  // short E
  'bed', 'red', 'get', 'pen', 'ten',
  // short I
  'big', 'pig', 'sit', 'hit', 'win',
  // short O
  'dog', 'hop', 'top', 'hot', 'mom',
  // short U
  'sun', 'run', 'bug', 'hug', 'cup',
];

// Grade 1 (30 words) — 6 per short vowel. Same patterns, slightly less
// common words mixed in for variety + fluency-building.
const GRADE_1 = [
  // short A
  'cat', 'hat', 'fan', 'can', 'jam', 'tag',
  // short E
  'bed', 'get', 'pen', 'ten', 'leg', 'web',
  // short I
  'big', 'pig', 'sit', 'win', 'dig', 'lip',
  // short O
  'dog', 'hop', 'top', 'fox', 'pot', 'job',
  // short U
  'sun', 'run', 'bug', 'cup', 'mud', 'jug',
];

// Grade 2–3 Intervention (30 words) — same difficulty band as Grade 1.
// Different word selection so a teacher can rotate without students
// memorizing one fixed list. The label "(Intervention)" is honest:
// fluent Grade 2–3 readers don't need a CVC measure; they're ready for
// CVCe, vowel teams, and r-controlled vowels.
const GRADE_2_3_INTERVENTION = [
  // short A
  'mat', 'bat', 'sat', 'ham', 'tap', 'lap',
  // short E
  'red', 'jet', 'net', 'wet', 'hen', 'men',
  // short I
  'hit', 'fit', 'kit', 'fix', 'dim', 'rim',
  // short O
  'mop', 'cob', 'rob', 'log', 'got', 'box',
  // short U
  'hug', 'fun', 'gum', 'nut', 'bus', 'mug',
];

// Mixed (25 words) — balanced 5-per-vowel sample drawn from all four grade
// bands. For multi-age classes, regression checks, and teachers who want
// variety without configuring a custom list. Each vowel pulls from a mix
// of difficulty levels.
const MIXED = [
  // short A — preK, K, G1, G2-3, G2-3
  'cat', 'fan', 'jam', 'ham', 'tap',
  // short E — K, K, G1, G1, G2-3
  'bed', 'get', 'leg', 'web', 'jet',
  // short I — preK, preK, G1, G1, G2-3
  'pig', 'sit', 'dig', 'lip', 'fix',
  // short O — K, K, G1, G1, G2-3
  'dog', 'top', 'fox', 'pot', 'mop',
  // short U — K, K, G1, G1, G2-3
  'sun', 'bug', 'mud', 'jug', 'gum',
];

export const CVC_LISTS = {
  preK:                   PRE_K,
  kindergarten:           KINDERGARTEN,
  grade1:                 GRADE_1,
  grade2_3_intervention:  GRADE_2_3_INTERVENTION,
  mixed:                  MIXED,
};

// CVC pattern: exactly 3 letters, consonant-vowel-consonant where the
// vowel is one of a/e/i/o/u (short vowel sound by default).
// Used to validate teacher-provided custom word lists.
export function isValidCvc(word) {
  if (typeof word !== 'string') return false;
  const w = word.trim().toLowerCase();
  return /^[bcdfghjklmnpqrstvwxyz][aeiou][bcdfghjklmnpqrstvwxyz]$/.test(w);
}

// Returns the list of words for a given grade level. For 'custom', returns
// the teacher-provided customWords (validated upstream). Falls back to the
// kindergarten list if the grade level is unrecognized.
export function getCvcWords(gradeLevel, customWords) {
  if (gradeLevel === 'custom' && Array.isArray(customWords) && customWords.length > 0) {
    return customWords.filter(isValidCvc);
  }
  return CVC_LISTS[gradeLevel] || CVC_LISTS.kindergarten;
}

// Returns the vowel of a CVC word (a/e/i/o/u). Used by the "Grouped by
// vowel" word-order option on the assessment.
export function vowelOf(word) {
  if (typeof word !== 'string' || word.length < 2) return null;
  const v = word.charAt(1).toLowerCase();
  return ['a', 'e', 'i', 'o', 'u'].includes(v) ? v : null;
}

export const CVC_WORD_ORDERS = ['random', 'fixed', 'byVowel'];
